import { useEffect, useMemo, useRef, useState } from "react";
import type { LucideIcon } from "lucide-react";
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Ban,
  Building2,
  ChevronsUpDown,
  CreditCard,
  FileWarning,
  LayoutGrid,
  List,
  Phone,
  Search,
  Shield,
  ShieldOff,
  Ticket,
  TicketX,
  UserCheck,
  Users,
  X,
} from "lucide-react";

import { api, assetUrl } from "@/lib/api";
import type { BusinessListing } from "@/types";
import BuyerProfileModal from "@/components/BuyerProfileModal";
import AdvisorProfileModal from "@/components/AdvisorProfileModal";
import AdvisorPeerDirectory from "@/components/AdvisorPeerDirectory";
import { useAuth } from "@/contexts/AuthContext";
import { ADMIN_ROLES } from "@/lib/roles";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Loader from "@/components/ui/loader";
import { cn, formatPhone, formatDate, formatDateTime, titleCase } from "@/lib/utils";
import { Pagination } from "@/components/ui/pagination";
import {
  EmptyPanel,
  MetricTile,
  PageHero,
  PagePanel,
  PageShell,
  PanelHeading,
} from "@/components/interior-page";

const PAGE_SIZE = 15;

const STATUS_COLORS: Record<string, string> = {
  active: "bg-emerald-500/15 text-emerald-600",
  inactive: "bg-slate-100 text-slate-600",
  pending_verification: "bg-amber-500/15 text-amber-600",
};

const STATUS_LABELS: Record<string, string> = {
  active: "Active",
  inactive: "Inactive",
  pending_verification: "Pending Verification",
};

// Human label for each clickable buyer-KPI tile filter (matches the API's segment keys).
const SEGMENT_LABELS: Record<string, string> = {
  active_subscriptions: "Active Subscriptions",
  paid: "Paid Subscriptions",
  granted: "Promo / Comp Access",
  cancelled: "Cancelled",
  past_due: "Past Due",
  with_promo: "With Promo Code",
  promo_expired: "Promo Expired",
  basic_free: "Basic / Free",
  no_nda: "No NDA",
};

interface DirectoryUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  status: string;
  email_verified: boolean;
  created_at: string | null;
  avatar_url: string | null;
  is_new: boolean;
  org_id?: number | null;
  org_name?: string | null;
  // advisors
  listing_count?: number;
  // buyers
  tier_name?: string | null;
  tier_amount?: number | null;
  subscription_active?: boolean;
  subscription_status?: string | null;
  access_expires_at?: string | null;
  is_stripe?: boolean;
  // Subscription window. `subscription_ends_at` is the scheduled cancellation
  // date when one is set, otherwise the renewal / promo-expiry date — null for
  // a healthy Stripe sub, whose period end Stripe owns and we don't persist.
  subscription_started_at?: string | null;
  subscription_ends_at?: string | null;
  cancel_at_period_end?: boolean;
  // Backend's verdict on whether the plan still grants access (a lapsed promo
  // grant keeps is_active = true, so this is not the same as subscription_active).
  subscription_live?: boolean;
  last_login_at?: string | null;
  last_activity_at?: string | null;
  last_nda_signed_at?: string | null;
  promo_code?: string | null;
  promo_redeemed_at?: string | null;
  promo_access_days?: number | null;
  promo_expires_at?: string | null;
  promo_expired?: boolean;
  // Whether the CODE is still enabled for new redemptions — unrelated to
  // whether this buyer's granted access has run out.
  promo_code_enabled?: boolean;
}

type Envelope = { total_elements: number; data: DirectoryUser[] };

/** An organization the viewer may see, with its directory totals. A scoped
 *  caller (tenant admin) gets exactly one row, which is how the UI decides
 *  whether an org filter is worth showing at all. */
interface DirectoryOrg {
  id: number;
  name: string;
  slug: string;
  is_default: boolean;
  buyer_count: number;
  advisor_count: number;
}

/**
 * Short form for the Org column — "BusinessLocating" is too long to repeat on
 * every row, and reads worse than "BL".
 *
 * Two derived rules, no per-org hardcoding, so orgs an admin creates later get
 * a sensible badge for free:
 *   1. a slug that is already an acronym wins ("ecfa" -> ECFA);
 *   2. otherwise the capitals in the name ("BusinessLocating" -> BL,
 *      "Acme Deal Partners" -> ADP).
 * A name with fewer than two capitals yields nothing readable, so that org
 * keeps its full name rather than getting a one-letter badge.
 */
function orgShortName(org: DirectoryOrg): string {
  if (org.slug.length <= 5) return org.slug.toUpperCase();
  const initials = (org.name.match(/[A-Z]/g) ?? []).join("");
  return initials.length >= 2 ? initials : org.name;
}

interface BuyerStats {
  total: number;
  active_subscriptions: number;
  // Live access split by who pays: paid (Stripe) + granted (promo / manual
  // comp) + basic_free always equals total.
  paid: number;
  paid_ending: number;
  granted: number;
  granted_promo: number;
  cancelled: number;
  past_due: number;
  with_promo: number;
  promo_expired: number;
  promo_converted: number;
  basic_free: number;
  no_nda: number;
}

type KpiTone = "primary" | "emerald" | "sky" | "rose" | "amber" | "violet" | "slate";

const KPI_TONES: Record<KpiTone, { chip: string; bar: string; glow: string }> = {
  primary: { chip: "bg-primary/10 text-primary", bar: "from-primary to-primary/60", glow: "bg-primary/10" },
  emerald: { chip: "bg-emerald-500/15 text-emerald-600", bar: "from-emerald-500 to-emerald-300", glow: "bg-emerald-500/15" },
  sky: { chip: "bg-primary/10 text-primary", bar: "from-primary to-primary/60", glow: "bg-primary/10" },
  rose: { chip: "bg-rose-500/15 text-rose-600", bar: "from-rose-500 to-rose-300", glow: "bg-rose-500/15" },
  amber: { chip: "bg-amber-500/15 text-amber-600", bar: "from-amber-500 to-amber-300", glow: "bg-amber-500/15" },
  violet: { chip: "bg-violet-500/15 text-violet-600", bar: "from-violet-500 to-violet-300", glow: "bg-violet-500/15" },
  slate: { chip: "bg-slate-100 text-slate-600", bar: "from-slate-400 to-slate-300", glow: "bg-slate-200/40" },
};

/** Polished KPI card for the buyer directory header: a tinted icon chip, a
 * gradient top-accent, and an optional sub-line (e.g. share of all buyers). */
function BuyerKpiTile({
  label,
  value,
  icon: Icon,
  tone,
  hint,
  onClick,
  active = false,
}: {
  label: string;
  value: React.ReactNode;
  icon: LucideIcon;
  tone: KpiTone;
  hint?: string;
  onClick?: () => void;
  active?: boolean;
}) {
  const t = KPI_TONES[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "group relative w-full overflow-hidden rounded-2xl border bg-white p-5 text-left shadow-sm transition-all duration-200",
        onClick && "cursor-pointer hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
        active ? "border-primary ring-2 ring-primary/35" : "border-border/70",
      )}
    >
      <span className={cn("absolute inset-x-0 top-0 h-1 bg-gradient-to-r", t.bar)} />
      <div className={cn("pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full blur-2xl transition-opacity duration-200 opacity-60 group-hover:opacity-100", t.glow)} />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">{label}</div>
          <div className="mt-2.5 text-[1.75rem] font-black leading-none tracking-tight text-foreground">{value}</div>
          {hint && <div className="mt-2 text-[0.7rem] font-semibold text-muted-foreground">{hint}</div>}
        </div>
        <span className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-xl", t.chip)}>
          <Icon className="h-5 w-5" />
        </span>
      </div>
    </button>
  );
}

/** Buyer plan cell: tier name + monthly price, with a status badge for
 * past-due / cancelled plans so they don't read as active. Promo / manual
 * access (no Stripe sub) carries an expiry date — surfaced here, and flagged
 * red once it has lapsed (the buyer redeemed a code but never subscribed). */
function PlanCell({ r, nowMs }: { r: DirectoryUser; nowMs: number }) {
  // No subscription row = the free tier, which the product calls "Basic"
  // (see lib/access.ts::isFreeBuyerUser). There is no Basic row in `tiers` —
  // it is the absence of a paid plan — so it's labelled here rather than
  // fetched, and styled flat to read as a floor, not an active paid plan.
  if (!r.tier_name) {
    return (
      <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-[0.65rem] font-bold uppercase tracking-[0.1em] text-slate-600">
        Basic
      </span>
    );
  }
  const price = r.tier_amount != null ? ` · $${r.tier_amount}/mo` : "";
  const status = r.subscription_status;
  // A promo / manual grant keeps status "active" after its paid-through date —
  // nothing flips it, the lapse is computed server-side into subscription_live.
  // Without this the chip would stay green for a plan that grants nothing, and
  // the row would contradict the "Basic / Free" tile that counts it.
  const lapsed = r.subscription_live === false && status === "active";
  const tone =
    lapsed
      ? "bg-slate-100 text-slate-500"
      : status === "active"
      ? "bg-emerald-500/15 text-emerald-600"
      : status === "past_due"
      ? "bg-amber-500/15 text-amber-600"
      : "bg-rose-500/15 text-rose-600";
  const statusLabel = lapsed
    ? "Expired — no access"
    : status === "past_due"
    ? "Past due"
    : status === "canceled"
    ? "Cancelled"
    : null;

  // Promo / manually-granted access expires on access_expires_at. Stripe-managed
  // subscriptions renew automatically, so no expiry is shown for them.
  const expiresAt = !r.is_stripe && r.access_expires_at ? new Date(r.access_expires_at) : null;
  const expired = expiresAt != null && expiresAt.getTime() < nowMs;
  const expiryLabel = expiresAt
    ? expiresAt.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
    : "";

  return (
    <div className="flex flex-col items-start gap-1">
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[0.65rem] font-bold uppercase tracking-[0.1em] ${tone}`}>
        {r.tier_name}{price}
      </span>
      {statusLabel && (
        <span className="text-[0.6rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground">{statusLabel}</span>
      )}
      {expiresAt && (
        <span className={`flex flex-col text-[0.6rem] font-semibold uppercase tracking-[0.1em] ${expired ? "text-rose-600" : "text-amber-600"}`}>
          <span>{expired ? "Promo expired" : "Promo until"}</span>
          <span className="whitespace-nowrap">{expiryLabel}</span>
        </span>
      )}
    </div>
  );
}

/** Small stacked cell: a date on top, a short qualifier under it. */
function DateStack({ date, note, tone }: { date: string; note?: string; tone?: string }) {
  return (
    <div className="flex flex-col items-start gap-0.5">
      <span className="text-xs text-muted-foreground">{date}</span>
      {note && (
        <span className={cn("text-[0.6rem] font-semibold uppercase tracking-[0.1em]", tone ?? "text-muted-foreground")}>
          {note}
        </span>
      )}
    </div>
  );
}

/**
 * When the buyer's current plan runs out.
 *
 * Three different things can end a subscription, and they must not look alike:
 *   - a cancellation the buyer already scheduled: a hard stop date, in red;
 *   - a promo / manual grant's paid-through date: amber, red once past;
 *   - a healthy Stripe subscription: no stop date at all.
 *
 * The last case genuinely has no date to show. Stripe owns the period end and
 * we never persist it (only cancel_at_period_end / cancels_at are written back,
 * by the in-app cancel flow), so it reads "Auto-renews" rather than an em dash
 * that would be indistinguishable from "no plan at all".
 */
function SubEndCell({ r, nowMs }: { r: DirectoryUser; nowMs: number }) {
  if (!r.subscription_started_at) return <span className="text-xs text-muted-foreground">—</span>;

  if (r.cancel_at_period_end && r.subscription_ends_at) {
    const past = new Date(r.subscription_ends_at).getTime() < nowMs;
    return (
      <DateStack
        date={formatDate(r.subscription_ends_at)}
        note={past ? "Cancelled" : "Cancels"}
        tone="text-rose-600"
      />
    );
  }

  // Only reachable when the Stripe mirror has not caught up yet (a sub created
  // since the last customer.subscription.updated event): Stripe owns the period
  // end, so say it renews rather than showing an em dash indistinguishable from
  // "no plan at all".
  if (!r.subscription_ends_at) {
    return r.is_stripe ? (
      <span className="text-[0.6rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground">Auto-renews</span>
    ) : (
      <span className="text-xs text-muted-foreground">—</span>
    );
  }

  const date = formatDate(r.subscription_ends_at);
  const past = new Date(r.subscription_ends_at).getTime() < nowMs;

  // A dead plan's stored date is when it stopped, not when it renews.
  if (r.subscription_status === "canceled") return <DateStack date={date} note="Ended" tone="text-rose-600" />;
  if (r.subscription_status === "past_due") return <DateStack date={date} note="Past due" tone="text-amber-600" />;

  // Stripe collects on its own, so a date in the past means our mirror is stale
  // (a renewal webhook we never processed) — worth flagging, not hiding.
  if (r.is_stripe) {
    return <DateStack date={date} note={past ? "Renewal due" : "Renews"} tone={past ? "text-amber-600" : undefined} />;
  }

  return (
    <DateStack
      date={date}
      note={past ? "Expired" : "Expires"}
      tone={past ? "text-rose-600" : "text-amber-600"}
    />
  );
}

/**
 * The buyer's latest promo redemption and whether it still grants anything.
 *
 * "Expired" is about THIS buyer's access window running out. A disabled code is
 * a separate fact — an admin can switch a code off to stop new redemptions
 * without touching access already granted — so it gets its own line rather than
 * being folded into the same badge.
 */
function PromoCell({ r }: { r: DirectoryUser }) {
  if (!r.promo_code) return <span className="text-muted-foreground">—</span>;
  const expired = Boolean(r.promo_expired);
  return (
    <div className="flex flex-col items-start gap-1">
      <span
        className={cn(
          "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[0.65rem] font-bold uppercase tracking-[0.1em]",
          expired ? "bg-slate-100 text-slate-500" : "bg-violet-500/15 text-violet-600",
        )}
      >
        <Ticket className="h-3 w-3" />
        {r.promo_code}
      </span>
      <span
        className={cn(
          "flex flex-col text-[0.6rem] font-semibold uppercase tracking-[0.1em]",
          expired ? "text-rose-600" : "text-emerald-600",
        )}
      >
        <span>{expired ? "Expired" : r.promo_expires_at ? "Active until" : "Active"}</span>
        {r.promo_expires_at && <span className="whitespace-nowrap">{formatDate(r.promo_expires_at)}</span>}
      </span>
      {!r.promo_code_enabled && (
        <span className="text-[0.6rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
          Code disabled
        </span>
      )}
    </div>
  );
}

interface AdvisorProfile {
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  avatar_url: string | null;
  bio: string | null;
  agency_name: string | null;
  years_of_experience: number | null;
  license_number: string | null;
  website_url: string | null;
  verified: boolean;
}

export default function UserDirectoryPage({ kind }: { kind: "advisors" | "buyers" }) {
  const { hasRole } = useAuth();
  // A BL advisor browsing their peers gets the follow-card network (name, photo,
  // bio, followers only). The record directory below is for admins.
  if (kind === "advisors" && !hasRole(...ADMIN_ROLES)) return <AdvisorPeerDirectory />;
  return <RecordDirectory kind={kind} />;
}

function RecordDirectory({ kind }: { kind: "advisors" | "buyers" }) {
  const isAdvisors = kind === "advisors";
  const labels = isAdvisors
    ? {
        eyebrow: "BL Advisors",
        title: "Every BL Advisor on the platform, in one directory.",
        description:
          "Browse all BL advisors with their contact details, account status, and listing activity. Newly registered advisors are highlighted until you review them.",
        metric: "Total Advisors",
        empty: "No advisors found",
      }
    : {
        eyebrow: "Buyers",
        title: "Every buyer on the platform, in one directory.",
        description:
          "Browse all buyers with their contact details, account status, and subscription plan. Newly registered buyers are highlighted until you review them.",
        metric: "Total Buyers",
        empty: "No buyers found",
      };

  const [rows, setRows] = useState<DirectoryUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  // Active buyer-KPI tile filter (e.g. "no_nda"); null shows every buyer.
  const [segment, setSegment] = useState<string | null>(null);
  // Column sort: null key = backend default (newest first). Sorting runs
  // server-side so it spans every page, not just the rows currently loaded.
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [selectedBuyerId, setSelectedBuyerId] = useState<string | null>(null);
  const [advisorListing, setAdvisorListing] = useState<BusinessListing | null>(null);
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"grid" | "table">("table");
  const [buyerStats, setBuyerStats] = useState<BuyerStats | null>(null);
  // Orgs the viewer may see, and the active org filter (null = all of them).
  const [orgs, setOrgs] = useState<DirectoryOrg[]>([]);
  const [orgFilter, setOrgFilter] = useState<number | null>(null);
  // Captured once at mount so promo-expiry comparisons stay pure across renders.
  const [nowMs] = useState(() => Date.now());
  const toast = useToast();
  // Monotonic id per fetch: a slow response that has been superseded by a newer
  // request must not overwrite fresher rows (auto-search fires while typing).
  const requestSeq = useRef(0);
  // The search text the current result set reflects. The auto-search effect
  // compares against this so submits / Clear filters don't trigger a second,
  // redundant fetch for a query that is already applied.
  const appliedSearchRef = useRef("");

  useEffect(() => {
    // A fresh directory starts unsorted (newest first); some columns differ
    // between advisors and buyers, so don't carry a filter or sort across the switch.
    setSortKey(null);
    setSortDir("desc");
    setSearchQuery("");
    setSegment(null);
    setOrgFilter(null);
    fetchRows(1, { search: "", segment: null, sort: null, dir: "desc", org: null });
    if (!isAdvisors) fetchBuyerStats(null);
    fetchOrgs();
    // Mark this directory as seen so the sidebar badge clears. The list we just
    // requested still reflects the previous last-seen, so new rows stay
    // highlighted for this visit.
    api.patch(`/directory/${kind}/mark-seen`, {}).catch(() => {});
  }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tiles follow the org chip, so they always describe the rows below them.
  const statsSeq = useRef(0);
  async function fetchBuyerStats(org: number | null = orgFilter) {
    const seq = ++statsSeq.current;
    try {
      const qs = org != null ? `?org=${org}` : "";
      const stats = await api.get<BuyerStats>(`/directory/buyers/stats${qs}`);
      if (seq === statsSeq.current) setBuyerStats(stats);
    } catch {
      /* KPI tiles are non-critical — fail silently and leave them blank. */
    }
  }

  async function fetchOrgs() {
    try {
      setOrgs(await api.get<DirectoryOrg[]>("/directory/orgs"));
    } catch {
      /* Without this the org filter and column simply don't render — the
         directory itself still works, so don't surface an error. */
    }
  }

  // Filters are passed explicitly so a freshly-set search/segment takes effect
  // immediately, without waiting for the state update to flush into this closure.
  async function fetchRows(
    targetPage: number = page,
    opts: {
      search?: string;
      segment?: string | null;
      sort?: string | null;
      dir?: "asc" | "desc";
      org?: number | null;
    } = {},
  ) {
    const effSearch = opts.search ?? searchQuery;
    const effSegment = opts.segment !== undefined ? opts.segment : segment;
    const effSort = opts.sort !== undefined ? opts.sort : sortKey;
    const effDir = opts.dir ?? sortDir;
    const effOrg = opts.org !== undefined ? opts.org : orgFilter;
    const seq = ++requestSeq.current;
    appliedSearchRef.current = effSearch;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (effSearch) params.set("search", effSearch);
      if (effSegment) params.set("segment", effSegment);
      if (effOrg != null) params.set("org", String(effOrg));
      if (effSort) {
        params.set("sort", effSort);
        params.set("order", effDir);
      }
      params.set("skip", String((targetPage - 1) * PAGE_SIZE));
      params.set("limit", String(PAGE_SIZE));
      const data = await api.get<Envelope>(`/directory/${kind}?${params.toString()}`);
      if (seq !== requestSeq.current) return; // superseded by a newer fetch
      setRows(data.data);
      setTotal(data.total_elements);
      setPage(targetPage);
    } catch {
      if (seq === requestSeq.current) toast.error(`Failed to load ${kind}`);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }

  // Auto-search: refetch shortly after the user stops typing, no Search click
  // needed. Skipped when the input already matches the applied search (mount,
  // explicit submit, Clear filters), which would otherwise double-fetch.
  useEffect(() => {
    if (searchQuery === appliedSearchRef.current) return;
    const timer = setTimeout(() => {
      if (searchQuery !== appliedSearchRef.current) fetchRows(1, { search: searchQuery });
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    fetchRows(1);
  };

  // Toggle a buyer-KPI tile: clicking the active tile (or "Total Buyers") clears
  // the filter and shows everyone.
  const selectSegment = (next: string | null) => {
    const value = next === segment ? null : next;
    setSegment(value);
    fetchRows(1, { segment: value });
  };

  // Pick an organization to narrow the directory to; null shows every org the
  // viewer is allowed to see.
  const selectOrg = (next: number | null) => {
    setOrgFilter(next);
    fetchRows(1, { org: next });
    if (!isAdvisors) fetchBuyerStats(next);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSegment(null);
    setOrgFilter(null);
    fetchRows(1, { search: "", segment: null, org: null });
    if (!isAdvisors && orgFilter != null) fetchBuyerStats(null);
  };

  // Click a column header to sort by it. First click on a new column sorts
  // ascending; clicking the active column toggles asc ⇄ desc. Always jumps back
  // to page 1 so you see the new top of the list.
  const handleSort = (key: string) => {
    const dir: "asc" | "desc" = sortKey === key && sortDir === "asc" ? "desc" : "asc";
    setSortKey(key);
    setSortDir(dir);
    fetchRows(1, { sort: key, dir });
  };

  const hasFilters = Boolean(searchQuery || segment || orgFilter != null);
  // Only worth showing the org controls when the viewer can actually see more
  // than one org — a tenant admin is hard-scoped to their own and would just
  // get a one-option dropdown and a column of identical values.
  const showOrgControls = orgs.length > 1;
  const orgShortById = useMemo(
    () => new Map(orgs.map((o) => [o.id, orgShortName(o)])),
    [orgs],
  );
  /* Row's org as shown in the directory: the short form when we know the org,
     falling back to the name the row itself carries. */
  const orgLabel = (r: DirectoryUser) =>
    (r.org_id != null ? orgShortById.get(r.org_id) : undefined) ?? r.org_name ?? null;

  // A sortable <th>: renders the label as a button with an arrow showing the
  // current sort direction (faint up/down chevrons when the column is inactive).
  const sortHead = (label: string, key: string) => {
    const active = sortKey === key;
    const Icon = active ? (sortDir === "asc" ? ArrowUp : ArrowDown) : ChevronsUpDown;
    return (
      <th
        className="p-0 text-left"
        aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
      >
        <button
          type="button"
          onClick={() => handleSort(key)}
          className={cn(
            "flex w-full items-center gap-1.5 px-4 py-3 text-[0.7rem] font-bold uppercase tracking-[0.12em] transition-colors hover:text-foreground",
            active ? "text-foreground" : "text-muted-foreground",
          )}
        >
          {label}
          <Icon className={cn("h-3.5 w-3.5 shrink-0", active ? "text-foreground opacity-100" : "text-foreground/70")} />
        </button>
      </th>
    );
  };

  async function openProfile(r: DirectoryUser) {
    if (!isAdvisors) {
      setSelectedBuyerId(r.id);
      return;
    }
    setOpeningId(r.id);
    try {
      const p = await api.get<AdvisorProfile>(`/directory/advisors/${r.id}/profile`);
      setAdvisorListing({
        agent_name: `${p.first_name} ${p.last_name}`.trim(),
        agent_email: p.email,
        agent_phone: p.phone,
        agent_avatar_url: p.avatar_url,
        agent_verified: p.verified,
        agent_agency_name: p.agency_name,
        agent_bio: p.bio,
        agent_years_of_experience: p.years_of_experience,
        agent_license_number: p.license_number,
        agent_website_url: p.website_url,
      } as unknown as BusinessListing);
    } catch {
      toast.error("Failed to load advisor profile");
    } finally {
      setOpeningId(null);
    }
  }

  const metrics = useMemo(() => {
    const active = rows.filter((r) => r.status === "active").length;
    const fresh = rows.filter((r) => r.is_new).length;
    return { total, active, fresh };
  }, [rows, total]);

  // Sub-line for a buyer KPI tile: the metric's share of all buyers.
  const shareHint = (n: number | undefined): string | undefined => {
    const totalBuyers = buyerStats?.total ?? 0;
    if (n == null || !totalBuyers) return undefined;
    return `${Math.round((n / totalBuyers) * 100)}% of buyers`;
  };

  return (
    <PageShell>
      <PageHero
        eyebrow={labels.eyebrow}
        title={labels.title}
        description={labels.description}
        actions={
          <Button
            size="lg"
            className="rounded-full"
            onClick={() => {
              fetchRows(page);
              if (!isAdvisors) fetchBuyerStats();
            }}
          >
            Refresh
          </Button>
        }
        aside={
          <>
            <div className="text-[0.72rem] font-bold uppercase tracking-[0.22em] text-primary/70">{labels.eyebrow}</div>
            <div className="mt-3 text-3xl font-black tracking-tight text-foreground">{metrics.total} total</div>
            <p className="mt-2 text-sm leading-7 text-muted-foreground">
              {metrics.active} active on this page{metrics.fresh > 0 ? `, ${metrics.fresh} new` : ""}.
            </p>
          </>
        }
      />

      {isAdvisors ? (
        <section className="grid gap-4 sm:grid-cols-3">
          <MetricTile label={labels.metric} value={metrics.total} icon={Users} tone="primary" />
          <MetricTile label="Active (page)" value={metrics.active} icon={UserCheck} tone="emerald" />
          <MetricTile label="New (page)" value={metrics.fresh} icon={Shield} tone="amber" />
        </section>
      ) : (
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          <BuyerKpiTile
            label="Total Buyers"
            value={buyerStats?.total ?? metrics.total}
            icon={Users}
            tone="primary"
            hint={orgFilter != null ? `In ${orgs.find((o) => o.id === orgFilter)?.name ?? "this org"}` : "On the platform"}
            onClick={() => selectSegment(null)}
            active={!segment}
          />
          {/* Paid + Promo / Comp + Basic / Free partition every buyer, so the
              three always add up to Total Buyers. "Active" used to lump free
              promo / manual grants in with Stripe-paying buyers. A lapsed grant
              counts as Basic. There is no "On Trial" tile: Stripe's `trialing`
              is persisted as `active`, so it could only ever have read 0. */}
          <BuyerKpiTile
            label="Paid Subscriptions"
            value={buyerStats?.paid ?? "—"}
            icon={CreditCard}
            tone="emerald"
            hint={buyerStats ? [shareHint(buyerStats.paid), buyerStats.paid_ending ? `${buyerStats.paid_ending} ending` : null].filter(Boolean).join(" · ") : undefined}
            onClick={() => selectSegment("paid")}
            active={segment === "paid"}
          />
          <BuyerKpiTile
            label="Promo / Comp Access"
            value={buyerStats?.granted ?? "—"}
            icon={Ticket}
            tone="violet"
            hint={buyerStats ? `${buyerStats.granted_promo} promo · ${buyerStats.granted - buyerStats.granted_promo} manual grant` : undefined}
            onClick={() => selectSegment("granted")}
            active={segment === "granted"}
          />
          <BuyerKpiTile label="Basic / Free" value={buyerStats?.basic_free ?? "—"} icon={ShieldOff} tone="slate" hint={buyerStats ? [shareHint(buyerStats.basic_free), "no live plan"].filter(Boolean).join(" · ") : undefined} onClick={() => selectSegment("basic_free")} active={segment === "basic_free"} />
          <BuyerKpiTile label="Cancelled" value={buyerStats?.cancelled ?? "—"} icon={Ban} tone="rose" hint="Stripe plan ended" onClick={() => selectSegment("cancelled")} active={segment === "cancelled"} />
          <BuyerKpiTile label="Past Due" value={buyerStats?.past_due ?? "—"} icon={AlertTriangle} tone="amber" hint="Payment failed, access paused" onClick={() => selectSegment("past_due")} active={segment === "past_due"} />
          <BuyerKpiTile
            label="Promo Expired"
            value={buyerStats?.promo_expired ?? "—"}
            icon={TicketX}
            tone="rose"
            hint={buyerStats ? `Lapsed, not converted · ${buyerStats.promo_converted} converted to paid` : undefined}
            onClick={() => selectSegment("promo_expired")}
            active={segment === "promo_expired"}
          />
          <BuyerKpiTile label="No NDA" value={buyerStats?.no_nda ?? "—"} icon={FileWarning} tone="slate" hint={buyerStats ? [shareHint(buyerStats.no_nda), "current NDA unsigned"].filter(Boolean).join(" · ") : undefined} onClick={() => selectSegment("no_nda")} active={segment === "no_nda"} />
        </section>
      )}

      {/* Totals per organization — sits directly under the total so a platform
          admin can see the split at a glance, and doubles as the org filter so
          there is only one org control on the page rather than two. Hidden for
          org-scoped viewers, who only ever have one org. */}
      {showOrgControls && (
        <section className="rounded-2xl border border-border/70 bg-white p-4 shadow-sm">
          <div className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">
            {isAdvisors ? "Advisors" : "Buyers"} by organization
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => selectOrg(null)}
              aria-pressed={orgFilter == null}
              className={cn(
                "inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.12em] transition-colors",
                orgFilter == null
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted/60 text-muted-foreground hover:bg-muted",
              )}
            >
              All orgs
              <span className="tabular-nums opacity-80">
                {orgs
                  .reduce((sum, o) => sum + (isAdvisors ? o.advisor_count : o.buyer_count), 0)
                  .toLocaleString()}
              </span>
            </button>
            {orgs.map((o) => {
              const count = isAdvisors ? o.advisor_count : o.buyer_count;
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => selectOrg(o.id)}
                  aria-pressed={orgFilter === o.id}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.12em] transition-colors",
                    orgFilter === o.id
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted/60 text-muted-foreground hover:bg-muted",
                  )}
                >
                  <Building2 className="h-3.5 w-3.5 shrink-0" />
                  {o.name}
                  <span className="tabular-nums opacity-80">{count.toLocaleString()}</span>
                </button>
              );
            })}
          </div>
        </section>
      )}

      <PagePanel>
        <PanelHeading title="Search and filter" description="Locate records by name or email — results update as you type." />

        <form onSubmit={handleSearch}>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/55" />
            <Input
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="pl-10"
            />
          </div>
        </form>

        {hasFilters && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {orgFilter != null && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-primary">
                <Building2 className="h-3.5 w-3.5" />
                {orgs.find((o) => o.id === orgFilter)?.name ?? `Org ${orgFilter}`}
              </span>
            )}
            {segment && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-primary">
                {SEGMENT_LABELS[segment] ?? segment}
              </span>
            )}
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-1.5 rounded-full bg-destructive/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-destructive transition-colors duration-200 hover:bg-destructive/15"
            >
              <X className="h-3.5 w-3.5" />
              Clear filters
            </button>
          </div>
        )}
      </PagePanel>

      <PagePanel>
        <PanelHeading
          title={isAdvisors ? "Advisor records" : "Buyer records"}
          description="A complete, read-only directory. New signups are highlighted."
          actions={
            <div className="flex items-center gap-1 rounded-xl border border-border/80 bg-card p-1 self-start sm:self-auto">
              <button
                onClick={() => setViewMode("grid")}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                  viewMode === "grid"
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground",
                )}
                title="Grid view"
              >
                <LayoutGrid className="h-4 w-4" />
                <span className="hidden sm:inline">Grid</span>
              </button>
              <button
                onClick={() => setViewMode("table")}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                  viewMode === "table"
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground",
                )}
                title="Table view"
              >
                <List className="h-4 w-4" />
                <span className="hidden sm:inline">Table</span>
              </button>
            </div>
          }
        />

        {loading && rows.length === 0 ? (
          <Loader className="min-h-[26vh]" />
        ) : rows.length === 0 ? (
          <EmptyPanel
            icon={Users}
            title={labels.empty}
            description="Try adjusting your filters or search query to broaden the result set."
          />
        ) : viewMode === "grid" ? (
          <div className={cn("grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3", loading && "pointer-events-none opacity-60")}>
            {rows.map((r) => (
              <article
                key={r.id}
                onClick={() => openProfile(r)}
                className={cn(
                  "flex cursor-pointer flex-col gap-3 rounded-2xl border bg-white p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md",
                  r.is_new ? "border-emerald-500/30 bg-emerald-500/10" : "border-border/70",
                  openingId === r.id ? "pointer-events-none opacity-50" : "",
                )}
              >
                <div className="flex items-start gap-3">
                  {r.avatar_url ? (
                    <img
                      src={assetUrl(r.avatar_url)!}
                      alt={`${r.first_name} ${r.last_name}`}
                      className="h-11 w-11 shrink-0 rounded-xl object-cover"
                    />
                  ) : (
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-sm font-black text-primary">
                      {r.first_name?.[0]}{r.last_name?.[0]}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); openProfile(r); }}
                        className="truncate font-semibold text-primary hover:underline"
                      >
                        {titleCase(`${r.first_name} ${r.last_name}`)}
                      </button>
                      {r.is_new && (
                        <span className="shrink-0 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-[0.12em] text-emerald-600">New</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[0.65rem] font-bold uppercase tracking-[0.12em] ${STATUS_COLORS[r.status] || "bg-slate-100 text-slate-700"}`}>
                    {r.status === "active" && <UserCheck className="h-3 w-3" />}
                    {r.status === "pending_verification" && <Shield className="h-3 w-3" />}
                    {STATUS_LABELS[r.status] || r.status}
                  </span>
                  <span className={`rounded-full px-2.5 py-1 text-[0.65rem] font-bold uppercase tracking-[0.12em] ${r.email_verified ? "bg-emerald-500/10 text-emerald-600" : "bg-amber-500/10 text-amber-600"}`}>
                    {r.email_verified ? "Verified" : "Unverified"}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 border-t border-border/50 pt-3 text-xs">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <Phone className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{formatPhone(r.phone) || "—"}</span>
                  </div>
                  <div className="flex justify-end text-right">
                    {isAdvisors ? (
                      <span>
                        <span className="text-muted-foreground">Listings: </span>
                        <span className="font-semibold text-foreground">{r.listing_count ?? 0}</span>
                      </span>
                    ) : (
                      <PlanCell r={r} nowMs={nowMs} />
                    )}
                  </div>
                  <div className="text-muted-foreground">
                    <span className="font-medium text-foreground/70">Last NDA:</span> {formatDate(r.last_nda_signed_at)}
                  </div>
                  <div className="text-right text-muted-foreground">
                    <span className="font-medium text-foreground/70">Last login:</span> {formatDateTime(r.last_login_at)}
                  </div>
                  {showOrgControls && (
                    <div className="col-span-2 text-muted-foreground">
                      <span className="font-medium text-foreground/70">Org:</span>{" "}
                      <span title={r.org_name ?? undefined}>{orgLabel(r) || "—"}</span>
                    </div>
                  )}
                  {!isAdvisors && (
                    <>
                      <div className="text-muted-foreground">
                        <span className="font-medium text-foreground/70">Sub start:</span>{" "}
                        {formatDate(r.subscription_started_at)}
                      </div>
                      <div className="flex justify-end text-right">
                        <span className="text-muted-foreground">
                          <span className="font-medium text-foreground/70">Sub end:</span>{" "}
                        </span>
                        <SubEndCell r={r} nowMs={nowMs} />
                      </div>
                      <div className="col-span-2 flex items-start gap-1.5 text-muted-foreground">
                        <span className="font-medium text-foreground/70">Promo:</span>
                        <PromoCell r={r} />
                      </div>
                    </>
                  )}
                  <div className="col-span-2 text-muted-foreground">
                    Joined {formatDate(r.created_at)}
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className={cn("overflow-x-auto rounded-2xl border border-border/60", loading && "pointer-events-none opacity-60")}>
            <table className="w-full min-w-[1560px] whitespace-nowrap text-sm">
              <thead>
                <tr className="border-b border-border/60 bg-muted/40">
                  {sortHead(isAdvisors ? "Advisor" : "Buyer", "name")}
                  {sortHead("Phone", "phone")}
                  {sortHead("Joined", "joined")}
                  {sortHead(isAdvisors ? "Listings" : "Plan", isAdvisors ? "listings" : "plan")}
                  {showOrgControls && sortHead("Org", "org")}
                  {sortHead("Last NDA", "last_nda")}
                  {sortHead("Last Login", "last_login")}
                  {!isAdvisors && sortHead("Promo", "promo")}
                  {sortHead("Status", "status")}
                  {sortHead("Email", "email")}
                  {!isAdvisors && sortHead("Sub Start", "sub_start")}
                  {!isAdvisors && sortHead("Sub End", "sub_end")}
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40 bg-white">
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => openProfile(r)}
                    className={`cursor-pointer transition-all duration-150 hover:bg-primary/[0.04] hover:shadow-[inset_3px_0_0_hsl(41_58%_55%/0.35)] ${r.is_new ? "bg-emerald-500/10" : ""} ${openingId === r.id ? "opacity-50 pointer-events-none" : ""}`}
                  >
                    {/* Name + email */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {r.avatar_url ? (
                          <img
                            src={assetUrl(r.avatar_url)!}
                            alt={`${r.first_name} ${r.last_name}`}
                            className="h-9 w-9 shrink-0 rounded-xl object-cover"
                          />
                        ) : (
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-xs font-black text-primary">
                            {r.first_name?.[0]}{r.last_name?.[0]}
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); openProfile(r); }}
                              className="truncate font-semibold text-primary hover:underline"
                            >
                              {titleCase(`${r.first_name} ${r.last_name}`)}
                            </button>
                            {r.is_new && (
                              <span className="shrink-0 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-[0.12em] text-emerald-600">New</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Phone */}
                    <td className="px-4 py-3 text-xs text-muted-foreground">{formatPhone(r.phone) || "—"}</td>

                    {/* Joined */}
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {formatDate(r.created_at)}
                    </td>

                    {/* Listings (advisor) / Plan (buyer) */}
                    <td className="px-4 py-3 text-xs">
                      {isAdvisors ? (
                        <span className="font-semibold text-foreground">{r.listing_count ?? 0}</span>
                      ) : (
                        <PlanCell r={r} nowMs={nowMs} />
                      )}
                    </td>

                    {/* Org — which tenant this person belongs to */}
                    {showOrgControls && (
                      <td className="px-4 py-3">
                        {r.org_name ? (
                          <span
                            title={r.org_name}
                            className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-[0.65rem] font-bold uppercase tracking-[0.1em] text-primary"
                          >
                            <Building2 className="h-3 w-3" />
                            {orgLabel(r)}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                    )}

                    {/* Last NDA / Last Login (both) + Promo (buyer). Last login
                        carries the clock time — "when exactly" is the point of
                        the column; the other dates stay day-level. */}
                    <td className="px-4 py-3 text-xs text-muted-foreground">{formatDate(r.last_nda_signed_at)}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {/* Date over time, so the column stays narrow. */}
                      <div className="flex flex-col whitespace-nowrap">
                        {formatDateTime(r.last_login_at).split(", ").map((part) => (
                          <span key={part}>{part}</span>
                        ))}
                      </div>
                    </td>
                    {!isAdvisors && (
                      <td className="px-4 py-3 text-xs">
                        <PromoCell r={r} />
                      </td>
                    )}

                    {/* Status */}
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[0.65rem] font-bold uppercase tracking-[0.12em] ${STATUS_COLORS[r.status] || "bg-slate-100 text-slate-700"}`}>
                        {r.status === "active" && <UserCheck className="h-3 w-3" />}
                        {r.status === "pending_verification" && <Shield className="h-3 w-3" />}
                        {STATUS_LABELS[r.status] || r.status}
                      </span>
                    </td>

                    {/* Email verified */}
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium ${r.email_verified ? "text-emerald-600" : "text-amber-600"}`}>
                        {r.email_verified ? "Verified" : "Unverified"}
                      </span>
                    </td>

                    {/* Subscription window (buyer only) — when the current plan
                        started, and when it stops or renews. */}
                    {!isAdvisors && (
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {formatDate(r.subscription_started_at)}
                      </td>
                    )}
                    {!isAdvisors && (
                      <td className="px-4 py-3 text-xs">
                        <SubEndCell r={r} nowMs={nowMs} />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pagination page={page} totalElements={total} pageSize={PAGE_SIZE} onChange={fetchRows} />
      </PagePanel>

      {selectedBuyerId && (
        <BuyerProfileModal
          buyerId={selectedBuyerId}
          onClose={() => setSelectedBuyerId(null)}
          buyerIds={rows.map((r) => r.id)}
          onNavigate={setSelectedBuyerId}
        />
      )}
      {advisorListing && (
        <AdvisorProfileModal listing={advisorListing} onClose={() => setAdvisorListing(null)} />
      )}
    </PageShell>
  );
}
